'use client';

/**
 * src/components/TurnstileWidget.jsx
 *
 * Componente cliente reutilizable para renderizar el widget de Cloudflare
 * Turnstile. Centraliza:
 *   - Carga idempotente del script de CF (`api.js?render=explicit`)
 *   - Render explícito una sola vez por instancia (sin doble render)
 *   - Handlers de callback / expired-callback / error-callback que limpian
 *     el token cuando deja de ser válido
 *   - Cleanup del widget al desmontar (window.turnstile.remove) — eso evita
 *     callbacks tras unmount sin necesidad de un flag "cancelled" en JS.
 *
 * BUG FIX (2026-05-26 hotfix): la versión previa usaba `let cancelled = false`
 * en la closure del useEffect, y el callback chequeaba `if (!cancelled)` antes
 * de propagar el token al padre. Si por cualquier razón el useEffect se
 * ejecutaba dos veces (StrictMode, hot reload, double-mount por route
 * transition, etc.), el primer cleanup seteaba `cancelled = true` y el
 * callback del primer render quedaba mudo — CF inyectaba el token al input
 * hidden pero el padre nunca recibía la notificación. El botón quedaba
 * disabled forever. AHORA:
 *   1. No usamos `cancelled`. Confiamos en `window.turnstile.remove(id)` del
 *      cleanup — CF deja de disparar callbacks una vez removido el widget.
 *   2. Mantenemos `onTokenRef = useRef(onToken)` para tener SIEMPRE la última
 *      versión de `onToken` accesible desde la closure del callback (evita
 *      stale closure si el padre cambia el handler).
 *
 * Si `NEXT_PUBLIC_TURNSTILE_SITE_KEY` no está configurada, NO renderiza nada
 * y llama onToken('') una vez para que el padre sepa que está "skipeado".
 *
 * Uso:
 *   <TurnstileWidget action="login" onToken={setTurnstileToken} theme="dark" />
 *
 * El `action` es opcional pero recomendado: se envía a CF y se valida en
 * el backend para evitar reuso entre flows (token de signup → forgot, etc.).
 */

import { useEffect, useRef, useState } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export default function TurnstileWidget({
  action,
  onToken,
  theme = 'dark',
  size = 'normal', // 'normal' | 'compact' | 'invisible'
  className,
  style,
}) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const [skipped] = useState(!siteKey);

  // Mantener onTokenRef apuntando a la versión más reciente de onToken.
  // Sin deps → corre en cada render. Esto permite que el padre cambie el
  // handler sin re-mount del widget.
  useEffect(() => {
    onTokenRef.current = onToken;
  });

  // Aviso inicial al padre si la key no está → para que el form sepa
  // que no debe esperar token (no bloquear el submit button).
  useEffect(() => {
    if (skipped) {
      onTokenRef.current?.('');
    }
  }, [skipped]);

  useEffect(() => {
    if (skipped) return;

    let pollTimer = null;       // polling para esperar window.turnstile
    let getResponsePoll = null; // polling defensivo para getResponse()
    let lastNotifiedToken = '';
    let mountedWidgetId = null;

    // 1. Cargar el script de CF (idempotente).
    let scriptEl = document.querySelector(`script[src*="challenges.cloudflare.com/turnstile"]`);
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.src = SCRIPT_SRC;
      scriptEl.async = true;
      scriptEl.defer = true;
      document.head.appendChild(scriptEl);
    }

    // ── Notifica al padre solo si el token cambió (evita spam de setState) ──
    const notifyToken = (token) => {
      const value = typeof token === 'string' ? token : '';
      if (value === lastNotifiedToken) return;
      lastNotifiedToken = value;
      onTokenRef.current?.(value);
    };

    // ── Polling defensivo de getResponse() ──
    // FIX 2 (2026-05-26 hotfix #2): observado en producción que CF a veces
    // popula `<input name="cf-turnstile-response">` SIN llamar el callback
    // registrado (probablemente cuando CF entra en "managed silent" mode).
    // Sin callback, nuestro state React queda en '' y el botón submit
    // queda disabled aunque el token existe. Solución: además del callback,
    // pollear `window.turnstile.getResponse(widgetId)` cada 500ms hasta
    // 30s. Si CF llama el callback, notifyToken dedupea (lastNotifiedToken).
    const startGetResponsePoll = (widgetId) => {
      if (!widgetId || !window.turnstile?.getResponse) return;
      const startedAt = Date.now();
      const MAX_MS = 30_000;
      getResponsePoll = setInterval(() => {
        if (Date.now() - startedAt > MAX_MS) {
          clearInterval(getResponsePoll);
          getResponsePoll = null;
          return;
        }
        try {
          const t = window.turnstile.getResponse(widgetId);
          if (t && typeof t === 'string' && t.length > 10) {
            notifyToken(t);
            // No paramos el poll todavía — si el token expira, getResponse
            // devolverá '' y notifyToken propagará '' para que el padre
            // sepa. Paramos solo después de MAX_MS o en unmount.
          } else if (lastNotifiedToken && !t) {
            // El token se invalidó (expiró o reset). Notificar al padre.
            notifyToken('');
          }
        } catch { /* widget removido o CF caído — noop */ }
      }, 500);
    };

    // 2. Render del widget cuando window.turnstile esté disponible.
    const tryRender = () => {
      if (!window.turnstile || !containerRef.current) {
        pollTimer = setTimeout(tryRender, 150);
        return;
      }
      try {
        mountedWidgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          ...(action ? { action } : {}),
          // Callback "feliz": cuando CF SÍ lo llama, propagamos directo.
          callback: (token) => { notifyToken(token); },
          'expired-callback': () => { notifyToken(''); },
          'error-callback': () => { notifyToken(''); },
        });
        widgetIdRef.current = mountedWidgetId;
        // Lanza el polling defensivo en paralelo (cubre el caso de
        // callback no-disparado en modo managed).
        startGetResponsePoll(mountedWidgetId);
      } catch (e) {
        console.warn('[TurnstileWidget] render failed:', e?.message);
      }
    };
    tryRender();

    // 3. Cleanup: detener polls + remover widget de CF.
    return () => {
      if (pollTimer) clearTimeout(pollTimer);
      if (getResponsePoll) clearInterval(getResponsePoll);
      const idToRemove = widgetIdRef.current ?? mountedWidgetId;
      if (idToRemove && window.turnstile?.remove) {
        try { window.turnstile.remove(idToRemove); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipped, siteKey, action, theme, size]);

  if (skipped) return null;

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      // Hint para que screen readers no narren el iframe vacío hasta
      // que el widget renderice
      aria-label="Validación de seguridad anti-bots"
    />
  );
}
